import { initCoverage } from './table-experience.js';
const EASE = [.22, 1, .36, 1];
export function initMotion(root = document, motion = window.Motion) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 900px) and (pointer: fine)');
  let controls = [], stopScroll = null, hasEntered = false;
  const art = root.querySelector('[data-table-art]'), hero = root.querySelector('[data-table-hero]'), scene = art?.querySelector('.table-scene');
  function stop() {
    stopScroll?.(); stopScroll = null;
    controls.forEach(control => { control?.cancel?.(); }); controls = [];
    [art, scene, hero?.querySelector('h1'), hero?.querySelector('.hero-copy p'), hero?.querySelector('.hero-copy .button')].forEach(node => { if (node) { node.style.transform = ''; node.style.opacity = ''; } });
    root.querySelectorAll('[data-cart-flight]').forEach(node => node.remove());
    root.querySelector('[data-bag-target]')?.classList.remove('is-catching');
  }
  function sync() {
    stop(); const off = media.matches;
    document.documentElement.dataset.motion = off ? 'off' : 'on';
    document.dispatchEvent(new CustomEvent('latebite:motionchange', { detail: { off } }));
    if (off || !motion?.animate || !hero || !art) return;
    if (!hasEntered) {
      if (scene) controls.push(motion.animate(scene, { y: [12, 0], scale: [1.008, 1] }, { duration: .74, ease: EASE }));
      hasEntered = true;
    }
    if (desktop.matches && motion.scroll) {
      const sequence = motion.animate(art, { y: [0, 14], scale: [1, 1.006] }, { ease: 'linear' });
      controls.push(sequence); stopScroll = motion.scroll(sequence, { target: hero, offset: ['start start', 'end start'] });
    }
  }
  function onPointer(event) {
    if (media.matches || !desktop.matches || !motion?.animate || !scene) return;
    // Viewport-relative input avoids layout reads per pointer frame.
    controls.filter(control => control?.__pointer).forEach(control => control.cancel?.());
    controls = controls.filter(control => !control?.__pointer);
    const animation = motion.animate(scene, { x: (event.clientX / window.innerWidth - .5) * 6, y: (event.clientY / window.innerHeight - .5) * 4 }, { duration: .5, ease: EASE });
    animation.__pointer = true; controls.push(animation);
  }
  const onLeave = () => { if (scene && !media.matches && motion?.animate) controls.push(motion.animate(scene, { x: 0, y: 0 }, { duration: .6, ease: EASE })); };
  media.addEventListener('change', sync); desktop.addEventListener('change', sync);
  hero?.addEventListener('pointermove', onPointer); hero?.addEventListener('pointerleave', onLeave); sync();
  return () => { stop(); media.removeEventListener('change', sync); desktop.removeEventListener('change', sync); hero?.removeEventListener('pointermove', onPointer); hero?.removeEventListener('pointerleave', onLeave); };
}
export function initNavigation(root = document) {
  const selected = new URL(window.location.href).searchParams.get('filter.p.product_type');
  root.querySelectorAll('[data-category],[data-category-all]').forEach(link => {
    const active = link.hasAttribute('data-category-all') ? !selected : link.dataset.category === selected;
    if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
  });
  const menus = [...root.querySelectorAll('.mobile-menu')];
  const syncMenuLabel = menu => {
    const summary = menu.querySelector('summary');
    summary?.setAttribute('aria-label', menu.open ? document.body.dataset.textCloseMenu || 'Close navigation' : document.body.dataset.textOpenMenu || 'Open navigation');
    summary?.setAttribute('aria-expanded', String(menu.open));
  };
  const onToggle = event => syncMenuLabel(event.currentTarget);
  menus.forEach(menu => { syncMenuLabel(menu); menu.addEventListener('toggle', onToggle); });
  function closeMenu(menu) { menu.open = false; syncMenuLabel(menu); }
  function escape(event) {
    if (event.key !== 'Escape') return;
    const menu = root.querySelector('.mobile-menu[open]');
    if (menu) { closeMenu(menu); menu.querySelector('summary')?.focus(); }
  }
  function outside(event) {
    const menu = root.querySelector('.mobile-menu[open]');
    if (menu && (!menu.contains(event.target) || event.target.closest?.('nav a'))) closeMenu(menu);
  }
  const onFocusOut = event => {
    const menu = root.querySelector('.mobile-menu[open]');
    if (menu && event.relatedTarget && !menu.contains(event.relatedTarget)) closeMenu(menu);
  };
  root.addEventListener('keydown', escape); root.addEventListener('click', outside);
  root.addEventListener('focusout', onFocusOut);
  return () => { root.removeEventListener('keydown', escape); root.removeEventListener('click', outside); root.removeEventListener('focusout', onFocusOut); menus.forEach(menu => menu.removeEventListener('toggle', onToggle)); };
}
function loadSolidRibbon() {
  const url = new URL('./ribbon-solid.js', import.meta.url);
  url.search = new URL(import.meta.url).search;
  return import(/* @vite-ignore */ url.href);
}
export function initSolidRibbon(root = document, load = loadSolidRibbon) {
  const stages = [...root.querySelectorAll('[data-table-stage]')].filter(stage => stage.dataset.sceneFailed !== 'true');
  if (!stages.length) return () => {};
  const records = stages.map(stage => ({ stage, controller: new AbortController(), runtime: null }));
  const dispose = record => { record.controller.abort(); record.runtime?.dispose(); record.runtime = null; };
  const pending = Promise.resolve().then(load);
  for (const record of records) {
    const { stage, controller } = record;
    pending.then(async ({ mountSolidRibbon }) => {
      if (controller.signal.aborted || !stage.isConnected) return;
      const runtime = await mountSolidRibbon(stage, { signal: controller.signal });
      if (controller.signal.aborted || !stage.isConnected) runtime?.dispose();
      else record.runtime = runtime;
    }).catch(() => {
      if (controller.signal.aborted || !stage.isConnected) return;
      stage.dataset.sceneFailed = 'true';
      stage.style.setProperty('--reveal', '1.00000');
      stage.style.setProperty('--ribbon-turn', '0deg');
      const section = stage.closest('[data-scroll-ribbon]');
      if (section) section.dataset.enhanced = 'false';
      document.dispatchEvent(new CustomEvent('latebite:motionchange'));
    });
  }
  const onUnload = event => {
    for (const record of records) {
      if (event.target === record.stage || event.target.contains?.(record.stage)) dispose(record);
    }
  };
  document.addEventListener('shopify:section:unload', onUnload);
  return () => {
    document.removeEventListener('shopify:section:unload', onUnload);
    records.forEach(dispose);
  };
}
function boot() {
  window.__midnightUICleanup?.();
  if (!document.querySelector('[data-midnight-root]')) return;
  const cleanups = [initMotion(), initNavigation(), initCoverage()];
  window.__midnightUICleanup = () => cleanups.splice(0).reverse().forEach(fn => fn());
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
}

